import { createSlice, PayloadAction, type UnknownAction } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import { InvestorProfile } from '@/api/custom';
import { fetchInvestorsAllThunk, type FetchInvestorsAllArg } from './investorsThunk';
import { buildInvestorsContextKey } from './investorsContext';

export interface InvestorsState {
  investors: InvestorProfile[];
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  activeContextKey?: string | null;
  currentRequestId?: string | null;
}

type AddInvestorPayload = {
  investor: InvestorProfile;
  contextKey: string | null;
};

const initialState: InvestorsState = {
  investors: [],
  loading: 'idle',
  activeContextKey: null,
  currentRequestId: null,
};

const getRequestedContextKey = (
  arg: FetchInvestorsAllArg | undefined,
  fallbackContextKey: string | null | undefined,
) => arg?.contextKey ?? fallbackContextKey ?? null;

const activateInvestorsContext = (state: InvestorsState, contextKey: string | null) => {
  if ((state.activeContextKey ?? null) === contextKey) {
    return;
  }

  state.activeContextKey = contextKey;
  state.currentRequestId = null;
  state.investors = [];
  state.loading = 'idle';
};

const hasPayload = (action: UnknownAction): action is PayloadAction<unknown> => 'payload' in action;

const isSetUserAction = (action: UnknownAction): action is PayloadAction<unknown> =>
  action.type === 'userinfo/setUser' && hasPayload(action);

const isResetUserAction = (action: UnknownAction) => action.type === 'userinfo/resetUser';

const isInitializeUserFromStorageFulfilledAction = (
  action: UnknownAction,
): action is PayloadAction<unknown> =>
  action.type === 'userinfo/initializeFromStorage/fulfilled' && hasPayload(action);

const investorsSlice = createSlice({
  name: 'Investors',
  initialState,
  reducers: {
    setInvestors: (state, action: PayloadAction<InvestorProfile[]>) => {
      state.investors = action.payload;
    },
    addInvestor: (state, action: PayloadAction<AddInvestorPayload>) => {
      if (action.payload.contextKey !== (state.activeContextKey ?? null)) {
        return;
      }

      state.investors.push(action.payload.investor);
    },
    investorRemoved: (state, action: PayloadAction<string>) => {
      state.investors = state.investors.filter((investor) => investor.id !== action.payload);
      state.currentRequestId = null;
      state.loading = 'idle';
    },
    resetInvestors: (state) => {
      state.investors = [];
      state.loading = 'idle';
      state.activeContextKey = null;
      state.currentRequestId = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchInvestorsAllThunk.fulfilled, (state, action) => {
        if (
          state.currentRequestId !== action.meta.requestId ||
          action.payload.contextKey !== (state.activeContextKey ?? null)
        ) {
          return;
        }

        state.investors = action.payload.investors;
        state.loading = 'succeeded';
        state.currentRequestId = null;
      })
      .addCase(fetchInvestorsAllThunk.pending, (state, action) => {
        const requestedContextKey = getRequestedContextKey(action.meta.arg, state.activeContextKey);
        if ((state.activeContextKey ?? null) !== requestedContextKey) {
          state.investors = [];
          state.activeContextKey = requestedContextKey;
        }

        state.loading = 'pending';
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchInvestorsAllThunk.rejected, (state, action) => {
        const requestedContextKey = getRequestedContextKey(action.meta.arg, state.activeContextKey);
        if (
          state.currentRequestId !== action.meta.requestId ||
          requestedContextKey !== (state.activeContextKey ?? null)
        ) {
          return;
        }

        state.loading = 'failed';
        state.currentRequestId = null;
      })
      .addMatcher(isSetUserAction, (state, action) => {
        activateInvestorsContext(state, buildInvestorsContextKey(action.payload));
      })
      .addMatcher(isResetUserAction, (state) => {
        activateInvestorsContext(state, null);
      })
      .addMatcher(isInitializeUserFromStorageFulfilledAction, (state, action) => {
        activateInvestorsContext(state, buildInvestorsContextKey(action.payload));
      });
  },
});

export const { setInvestors, addInvestor, resetInvestors, investorRemoved } =
  investorsSlice.actions;

export const selectInvestors = (state: RootState) => state.investors.investors;
export const selectInvestorsLoadingState = (state: RootState) => state.investors.loading;
export const selectInvestorsNeedsFetch = (state: RootState) =>
  state.investors.activeContextKey != null &&
  state.investors.investors.length === 0 &&
  state.investors.currentRequestId == null &&
  state.investors.loading === 'idle';

export default investorsSlice.reducer;
